import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/mergeMap';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { isSubtaskHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import { getCardListId } from '../../../common/utils.tsx';
import {
	BOTTOM_OF_LIST,
	TOP_OF_LIST,
	BEFORE_CARD,
	BOTTOM_OF_CARD_GROUP,
	TRANSITION_ZONE,
} from '../../../model/constants.tsx';
import { MoveCardsActiveSprintType } from '../../../model/modals/types.tsx';
import type { CardId } from '../../../model/types.tsx';
import {
	type CardsMoveRequestPayload,
	type CardsMoveRequestAction,
	type CardsMoveAction,
	CARDS_MOVE,
	cardsMoveRequest,
} from '../../../state/actions/cards/move/index.tsx';
import { type OpenModalAction, openModal } from '../../../state/actions/modals/index.tsx';
import {
	getIsCompanyManaged,
	getRankCustomFieldId,
	isCmpKanbanProject,
	isCMPScrumBacklog,
} from '../../../state/selectors/app/index.tsx';
import {
	makeGetBottomCardIdFromCardGroup,
	makeGetCardGroupByParentId,
} from '../../../state/selectors/card-entries/index.tsx';
import {
	makeGetBottomCardIdFromCardList,
	makeGetTopCardIdFromCardList,
	getSelectedCards,
	getFlattenOrderedCardIds,
	getCardLists,
	makeGetCardListIdByCardId,
	getCards,
} from '../../../state/selectors/cards/index.tsx';
import { makeIsCardListActiveSprint } from '../../../state/selectors/sprints/index.tsx';
import { makeGetKanbanSingleColumnStatusId } from '../../../state/selectors/statuses/index.tsx';
import type { MiddlewareAPI, ActionsObservable, State } from '../../../state/types.tsx';
import {
	getCMPCardsToMove,
	getIsMovingSubtasks,
	getNewCardOrder,
	getNewCardOrderGlobal,
} from './utils.tsx';

const getBeforeCardId = (action: CardsMoveAction, topCardId: null | CardId) => {
	const { placement } = action.payload;
	if (placement === TOP_OF_LIST) {
		return topCardId || null;
	}
	if (action.payload.placement === BEFORE_CARD) {
		return action.payload.beforeCardId;
	}
	return null;
};

const getAfterCardId = (action: CardsMoveAction, bottomCardId: null | CardId) => {
	const { placement } = action.payload;
	if (placement === BOTTOM_OF_LIST || placement === BOTTOM_OF_CARD_GROUP) {
		return bottomCardId || null;
	}

	return null;
};

export const handleCMPCardTransitions = ({
	state,
	cardListId,
	cardIds,
	isDroppedInTransitionZone,
	beforeCardId,
	afterCardId,
	targetStatusId,
	analyticsEvent,
	newCardOrderGlobal,
}: {
	state: State;
	cardListId: string;
	cardIds: CardId[];
	isDroppedInTransitionZone: boolean;
	beforeCardId: CardId | null;
	afterCardId: CardId | null;
	targetStatusId?: string;
	analyticsEvent: UIAnalyticsEvent;
	newCardOrderGlobal: CardId[];
}): Observable<CardsMoveRequestAction> => {
	const newCardOrder = getNewCardOrder(
		state,
		cardListId,
		cardIds,
		beforeCardId,
		afterCardId,
		isDroppedInTransitionZone,
	);

	return Observable.of(
		cardsMoveRequest(
			{
				cardIds,
				cardListId,
				newCardOrder,
				newCardOrderGlobal,
				targetStatusId,
				beforeCardId,
				afterCardId,
				isBetweenLists: true,
			},
			analyticsEvent,
		),
	);
};

export const beforeMoveCardsEpic = (action$: ActionsObservable, store: MiddlewareAPI) =>
	action$
		.ofType(CARDS_MOVE)
		.mergeMap((action: CardsMoveAction): Observable<OpenModalAction | CardsMoveRequestAction> => {
			const state = store.getState();
			const rankCustomFieldId = getRankCustomFieldId(state);

			if (!rankCustomFieldId) {
				return Observable.empty<never>();
			}

			const {
				payload: {
					cardIds,
					cardListId: destinationCardListId,
					isBetweenLists,
					isFromActiveSprint,
					placement,
				},
				meta: { analyticsEvent },
			} = action;
			const cardListId = getCardListId(destinationCardListId);
			const cardOrder = getFlattenOrderedCardIds(state);
			const cardLists = getCardLists(state);
			const cards = getCards(state);
			const isCmpKanbanBacklog = isCmpKanbanProject(state);
			const isCmpScrumBacklog = isCMPScrumBacklog(state);
			const isCmpBacklog = getIsCompanyManaged(state);
			let cardsToMove = cardIds || getSelectedCards(state);
			let newCardOrderGlobal: CardId[] | undefined;

			const isMovingSubtasks = getIsMovingSubtasks(cards, cardsToMove);

			const cardParentId = cardsToMove
				.filter((card) => isSubtaskHierarchyLevel(cards[card]?.typeHierarchyLevel))
				.reduce<number | null>((id, curr) => cards[curr].parentId ?? id, null);

			const targetStatusId = isBetweenLists
				? makeGetKanbanSingleColumnStatusId(state)(cardListId)
				: undefined;

			if (isCmpBacklog) {
				const getCardListIdByCardId = makeGetCardListIdByCardId(state);
				const getCardGroupByParentId = makeGetCardGroupByParentId(state);
				cardsToMove = getCMPCardsToMove({
					cardIds: cardsToMove,
					getCardListIdByCardId,
					getCardGroupByParentId,
					isCmpScrumBacklog,
				});
			}

			const orderedCardIds = cardOrder.filter((orderedCardId) =>
				cardsToMove.includes(orderedCardId),
			);

			const topCardId = makeGetTopCardIdFromCardList(state)(cardListId, cardsToMove);
			const bottomCardId =
				isMovingSubtasks && cardParentId && placement !== BOTTOM_OF_LIST
					? makeGetBottomCardIdFromCardGroup(state)(cardListId, cardParentId, cardsToMove)
					: makeGetBottomCardIdFromCardList(state)(cardListId, cardsToMove);
			const beforeCardId = getBeforeCardId(action, topCardId);

			// When dropping on subtask and moving card is not a subtask set bottom as the parentId of the subtasks
			const bottomId =
				bottomCardId &&
				!isMovingSubtasks &&
				isSubtaskHierarchyLevel(cards[bottomCardId]?.typeHierarchyLevel)
					? cards[bottomCardId]?.parentId
					: bottomCardId;
			const afterCardId = getAfterCardId(action, bottomId);

			if (isCmpKanbanBacklog) {
				const isTransitionZoneDestination = action.payload.placement === TRANSITION_ZONE;

				newCardOrderGlobal = getNewCardOrderGlobal(
					state,
					orderedCardIds,
					beforeCardId,
					afterCardId,
					isMovingSubtasks,
				);

				if (isBetweenLists && (isTransitionZoneDestination || isMovingSubtasks)) {
					return handleCMPCardTransitions({
						state,
						cardListId,
						cardIds: orderedCardIds,
						isDroppedInTransitionZone: isTransitionZoneDestination,
						beforeCardId,
						afterCardId,
						targetStatusId,
						analyticsEvent,
						newCardOrderGlobal,
					});
				}
			}

			const payload: CardsMoveRequestPayload = {
				cardIds: orderedCardIds,
				cardListId,
				isBetweenLists,
				beforeCardId,
				afterCardId,
				targetStatusId,
				newCardOrderGlobal,
			};

			const toActiveSprint = makeIsCardListActiveSprint(state)(cardListId);

			if (isBetweenLists && (isFromActiveSprint || toActiveSprint)) {
				const originCardListId = makeGetCardListIdByCardId(state)(orderedCardIds[0]);
				return Observable.of(
					openModal({
						type: MoveCardsActiveSprintType,
						payload: {
							cardIds: orderedCardIds,
							originCardListName: originCardListId ? cardLists[originCardListId].name : '',
							destinationCardListName: cardLists[cardListId].name,
							confirmActionPayload: payload,
							analyticsEvent,
						},
					}),
				);
			}

			return Observable.of(cardsMoveRequest(payload, analyticsEvent));
		});
